
import React, { useState, useEffect } from 'react';
import { Box, Button, Modal, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import TrainerSearchSelect from '../../../common/TrainerSearchSelect';
import { FormInputTextArea } from '../../../../form-components/FormInputTextArea';
import { getPointTrainers } from '../../../../api/staff';
import { updateTicketInstructor, updateTrialTicketInstructor } from '../../../../api/member-management/index';
import { errorMessages } from '../../../../utils/methods';
import CloseIcon from '@mui/icons-material/Close';

const ChangeInstructorModal = ({ modal, setModal, bigUser, ticket, user, getAllTickets }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [trainers, setTrainers] = useState([]);
  // 일일권(체험권)은 memberTrialTicketId만 보유하고 memberCourseTicketId가 없다.
  const isTrial = !!ticket?.memberTrialTicketId;

  const { handleSubmit, control, reset } = useForm({
    defaultValues: {
      trainers: ticket?.trainerIds || [],
      memo: ticket?.trainerChangeMemo || ''
    }
  })

  const onClose = () => {
    setModal({ ...modal, open5: false });
  };

  // 담당 강사 조회
  const getTrainers = async () => {
    try {
      const param = `${user?.branchId || bigUser?.branch?.branchId}&courseClassType=PRIVATE_LESSON`;
      const data = await getPointTrainers(param, bigUser.token);

      const trainerArray = [
        { value: '', label: '담당 강사를 선택해주세요' },
        ...data.map(item => ({
          value: item.trainerId,
          label: item.trainerName,
        }))
      ];

      // 배정돼 있지만 재직 목록에 없는 강사(퇴사 등) — 옵션에 없으면 표시도 해제도 못 한다.
      // trainerIds 와 trainerName(나열)은 같은 배정 순서라 index 로 이름을 짝지을 수 있다.
      const assignedIds = ticket?.trainerIds || [];
      const assignedNames = (ticket?.trainerName || '').split(', ');
      assignedIds.forEach((id, index) => {
        if (!trainerArray.some(option => option.value === id)) {
          trainerArray.push({ value: id, label: `${assignedNames[index] || '알 수 없음'} (퇴사)` });
        }
      });

      setTrainers(trainerArray);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  //담당 강사 변경
  const submit = async (values) => {
    try {
        if (!values.trainers || values.trainers.length === 0) {
          toast.error('담당 강사를 선택해주세요');
          return;
        }

        // #77 다중 배정 — trainerIds 전량 교체, trainerId 는 구서버 호환용 첫 강사
        const update = {
            trainerId: values.trainers[0],
            trainerIds: values.trainers,
            memo: values.memo,
        }

      if (isTrial) {
        // 일일권은 member_trial_ticket 에 강사 컬럼이 없어 회원 하위 서브리소스로 배정만 교체한다.
        await updateTrialTicketInstructor(bigUser.token, user.memberId, ticket.memberTrialTicketId, update);
      } else {
        // memberId — 공유받은 회원이 보는 카드면 그 회원 몫의 담당강사만 바뀐다(소유자 카드는 소유자 ID 라 기존과 같다)
        await updateTicketInstructor(bigUser.token, { ...update, memberCourseTicketId: ticket.memberCourseTicketId, memberId: user.memberId });
      }

      toast.success('담당 강사가 변경되었습니다');
      getAllTickets();
      onClose();
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  useEffect(() => {
    if (modal.open5) {
      getTrainers();
      // 열 때마다 현재 배정으로 프리필 (다중 select)
      reset({ trainers: ticket?.trainerIds || [], memo: isTrial ? '' : ticket?.trainerChangeMemo || '' });
    }
  }, [modal.open5]);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '507px',
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column'
  };

  return (
    <Modal
      open={modal.open5}
      onClose={onClose}
      disableEscapeKeyDown
      aria-labelledby="instructor-modal-title"
      aria-describedby="instructor-modal-description"
    >
      <Box sx={modalStyle}>
        {/* 헤더 영역 */}
        <Typography
          id="instructor-modal-title"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: '20px 30px',
            borderBottom: '1px solid #e1e1e1'
          }}
        >
          <Box sx={{ fontSize: '18px' }}>담당 강사 설정</Box>
          <Button
            onClick={onClose}
            sx={{
              color: 'black',
              width: '40px',
              height: '40px',
              padding: '0px',
              border: '1px solid white',
              backgroundColor: 'white',
              minWidth: 'unset'
            }}
          >
            <CloseIcon sx={{ width: '20px', height: '20px' }} />
          </Button>
        </Typography>

        {/* 컨텐츠 영역 */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: '20px 30px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* 현재 강사 정보 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: '20px',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Typography className="title" sx={{ fontSize: '14px', mr: '25px' }}>
                현재 담당 강사
              </Typography>
              <Box
                sx={{
                  width: '280px',
                  p: '10px',
                  bgcolor: '#f5f5f5',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#666',
                }}
              >
                {ticket?.trainerName || '설정된 강사가 없습니다'}
              </Box>
            </Box>

            {/* 담당 강사 선택 - ManualModalV2 스타일에 맞춤 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                mb: '20px',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Typography className="title" sx={{ fontSize: '14px', mr: '25px', mt: '12px' }}>
                변경할 담당 강사
              </Typography>
              <TrainerSearchSelect
                control={control}
                name="trainers"
                options={trainers}
                selectAll
                sx={{ width: '280px' }}
              />
            </Box>

            {/* 메모 - ManualModalV2 스타일에 맞춤 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start', // 상단 정렬로 변경
                mb: '20px',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Typography className="title" sx={{ fontSize: '14px', mr: '25px', mt: '8px' }}>
                메모
              </Typography>
              <FormInputTextArea
                control={control}
                name="memo"
                placeholder="변경 사유나 메모를 입력해주세요"
                minRows={8}
                style={{
                  width: '280px',
                  height: '110px',
                  padding: '12px 13px 10px 11px',
                  fontSize: '13px',
                  fontWeight: '500',
                  border: '1px solid #AEAEAEB5',
                  borderRadius: '8px',
                  color: '#1f2329 !important',
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* 버튼 영역 */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            p: '20px 30px',
            borderTop: '1px solid #e1e1e1'
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: '#ddd',
              color: '#666',
              '&:hover': {
                borderColor: '#ddd',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(submit)}
            sx={{
              backgroundColor: '#0F7F85',
              '&:hover': {
                backgroundColor: '#0F7F85'
              }
            }}
          >
            변경
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ChangeInstructorModal;